import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import homeBg from '../asserts/homebg.mp4';
import './home.css';
import Preloader from '../components/layout/Preloader';
import { useMusic } from '../components/layout/MusicContext';

const Home = () => {
  const navigate = useNavigate();
  const [showPreloader, setShowPreloader] = useState(false);
  const [ready, setReady] = useState(false);
  const [count, setCount] = useState(0);
  const { startBgMusic } = useMusic();

  const titleRef = useRef(null);
  const bgRef = useRef(null);
  const overlayRef = useRef(null);
  const circleRef = useRef(null);
  const containerRef = useRef(null);
  const progressFillRef = useRef(null);
  const progressWrapRef = useRef(null);

  useEffect(() => {
    startBgMusic();
    const video = bgRef.current;

    const triggerTransition = () => {
      const circle = circleRef.current;
      const size = Math.max(window.innerWidth, window.innerHeight) * 2.6;

      gsap.set(circle, {
        width: 0,
        height: 0,
        opacity: 1,
        borderRadius: '50%',
        xPercent: -50,
        yPercent: -50,
        left: '50%',
        top: '100%',
      });

      gsap.to(circle, {
        width: size,
        height: size,
        duration: 2,
        ease: 'power2.inOut',
        onComplete: () => setShowPreloader(true),
      });
    };

    const startEverything = () => {
      setReady(true);
      const duration = video.duration || 12;

      const titleEl = titleRef.current;
      const words = titleEl.querySelectorAll('.word');

      gsap.set(bgRef.current, { scale: 1.12, opacity: 0 });
      gsap.set(overlayRef.current, { opacity: 1 });
      gsap.set(progressWrapRef.current, { opacity: 0, y: 40, scale: 0.85 });

      words.forEach((word) => {
        const letters = word.querySelectorAll('.letter');
        gsap.set(letters, {
          opacity: 0,
          x: () => (Math.random() - 0.5) * 600,
          y: () => (Math.random() - 0.5) * 400,
          rotation: () => (Math.random() - 0.5) * 180,
          scale: () => Math.random() * 1.5 + 0.2,
          filter: 'blur(6px)',
        });
      });

      const tl = gsap.timeline();

      tl.to(bgRef.current, {
        scale: 1,
        opacity: 1,
        duration: 4.5,
        ease: 'power2.out',
      }).to(
        overlayRef.current,
        { opacity: 0, duration: 3.5, ease: 'power2.out' },
        '-=3.5'
      );

      words.forEach((word, wi) => {
        const letters = word.querySelectorAll('.letter');
        tl.to(
          letters,
          {
            opacity: 1,
            x: 0,
            y: 0,
            rotation: 0,
            scale: 1,
            filter: 'blur(0px)',
            duration: 1.4,
            ease: 'back.out(1.4)',
            stagger: 0.06,
          },
          wi === 0 ? '-=0.6' : '-=0.8'
        );
      });

      const wordsDuration = tl.duration();
      const halfWordsDuration = wordsDuration * 1;

      gsap.to(titleRef.current, {
        y: -8,
        duration: 6,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: wordsDuration ,
      });

      gsap.to(progressWrapRef.current, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.4,
        ease: 'back.out(1.6)',
        delay: halfWordsDuration,
        onStart: () => {
          setTimeout(() => {
            video.play().catch(() => {});

            video.addEventListener('ended', () => {
              video.pause();
            }, { once: true });

            gsap.to(progressFillRef.current, {
              width: '100%',
              duration: duration,
              ease: 'none',
              onUpdate: function () {
                setCount(Math.floor(this.progress() * 100));
              },
              onComplete: () => {
                setCount(100);
                triggerTransition();
              },
            });
          }, 0);
        },
      });
    };

    if (video) {
      if (video.readyState >= 1) {
        startEverything();
      } else {
        video.addEventListener('loadedmetadata', startEverything, { once: true });
      }
    }

    return () => {
      if (video) video.pause();
    };
  }, []);

  if (showPreloader) {
    return (
      <Preloader
        onUserGesture={() => startBgMusic()}
        onComplete={() => navigate('/story')}
      />
    );
  }

  const line1 = ['The', "Pharaoh's"];
  const line2 = ['Shadow'];

  const renderLine = (words) =>
    words.map((word, wi) => (
      <React.Fragment key={wi}>
        {wi > 0 && <span className="word-space"> </span>}
        <span className="word">
          {word.split('').map((char, ci) => (
            <span key={ci} className="letter">{char}</span>
          ))}
        </span>
      </React.Fragment>
    ));

  return (
    <div className="home-container" ref={containerRef} style={{ visibility: ready ? 'visible' : 'hidden' }}>
      <div className="home-vignette" />
      <div className="home-overlay" ref={overlayRef} />
      <video
        className="home-bg"
        ref={bgRef}
        src={homeBg}
        muted
        playsInline
      />
      <div className="home-content">
        <h1 className="home-title" ref={titleRef}>
          <span className="title-line">{renderLine(line1)}</span>
          <span className="title-line">{renderLine(line2)}</span>
        </h1>
        <div className="home-progress-wrap" ref={progressWrapRef}>
          <div className="home-progress-bar">
            <div className="home-progress-fill" ref={progressFillRef} />
          </div>
          <span className="home-progress-count">{count}%</span>
        </div>
      </div>
      <div className="home-circle" ref={circleRef} />
    </div>
  );
};

export default Home;